import { GoogleAds, useScreenDetect } from '@sgcmpteltd/uikit-next';
import type {
  OnSlotRenderEnded,
} from '@sgcmpteltd/uikit-next/components/google-ads';
import {
  isValidElement,
  useContext,
  useMemo,
  useRef,
} from 'react';

import type {
  CamelCaseAdAlert,
  CamelCaseAdAlertList,
  CamelCaseCarAlert,
  CamelCaseCarAlertList,
} from '@/apis/server/new-car/v1/my-alert';
import { isCamelCaseCarAlert } from '@/apis/server/new-car/v1/my-alert/utils';
import { TeadAds } from '@/components/teads-ads';
import { teadsId, teadsTagId } from '@/configs/analytics';
import { listingBanner1, listingBanner2 } from '@/configs/google/ads/mobile';
import { AppContext } from '@/context/app';

import type { CompareItem } from '../../../types';
import { PromotionCarousel } from '../promotion-carousel';
import { CarCard } from './car-card';
import type { AlertList } from './car-card/types';
import styles from './styles.module.scss';
import type { Component } from './types';

export const SearchList: Component = ({
  list,
  myAlertList,
  compareItems,
  promotionList,
  searchTargetMapping,
  setMyAlert,
  onComparedCheckboxChange,
}) => {
  const appContextData = useContext(AppContext);
  const { isMobile, isIpadMini, isDesktop } = useScreenDetect();

  const isMobileMode = isMobile();
  const isIpadMiniMode = isIpadMini();
  const isDesktopMode = isDesktop();

  const onSlotRenderEndedRef = useRef<OnSlotRenderEnded>(({
    isEmpty,
    slot,
  }) => {
    if (!isEmpty) {
      return;
    }

    window.googletag.cmd.push(() => {
      window.googletag.pubads().refresh([
        slot,
      ]);
    });
  });

  const carCardListWithPromo = useMemo(() => {
    const handleAlertModalSubmit = (
      alert: CamelCaseAdAlert | CamelCaseCarAlert,
    ) => {
      if (isCamelCaseCarAlert(alert) && myAlertList) {
      // remove
        if (!alert.adCarPriceChange && !alert.piCarPromo) {
          const newCarAlert = myAlertList.carAlerts
            .filter(carAlert => carAlert.carId !== alert.carId);

          if (newCarAlert) {
            setMyAlert({ ...myAlertList, carAlerts: newCarAlert });
          }
        } else { // add
          setMyAlert({
            ...myAlertList,
            carAlerts: [...myAlertList.carAlerts, alert],
          });
        }
      }
    };

    const handleComparedCheckboxChange = (
      compareItem: CompareItem,
      isCompared: boolean,
    ) => {
      onComparedCheckboxChange?.(compareItem, isCompared);
    };

    let adAlerts: CamelCaseAdAlertList = [];
    let carAlerts: CamelCaseCarAlertList = [];

    if (myAlertList) {
      ({ adAlerts, carAlerts } = myAlertList);
    }

    const modelIdsWithPromotion = promotionList && promotionList
      .flatMap(item => item.models.map(model => model.id));

    const carList = list?.map((item, index) => {
      const matchingAdAlerts = adAlerts.filter(
        alert => alert.make === item.make,
      );

      const matchingCarAlerts = carAlerts.filter(
        alert => alert.carId === item.id,
      );

      const matchingPromotion = modelIdsWithPromotion
        && modelIdsWithPromotion.filter(
          id => id === item.id,
        );

      const hasPromotion = !!(matchingPromotion && matchingPromotion?.length > 0); // @suppress longLineCheck

      const alertList: AlertList = {
        adAlert: matchingAdAlerts.length
          ? matchingAdAlerts[0]
          : {
            make: item.make,
            adPromo: false,
            adCarLaunches: false,
          },
        carAlert: matchingCarAlerts.length
          ? matchingCarAlerts[0]
          : {
            carId: item.id,
            adCarPriceChange: false,
            piCarPromo: false,
          },
      };

      return (
        <CarCard
          key={item.id}
          adList={item.authorizedDealers}
          additionalProperties={item.additionalProperties}
          alertList={alertList}
          compareItems={compareItems}
          country={item.country}
          hasPromotion={hasPromotion}
          id={item.id}
          image={item.image}
          isPriority={index === 0}
          launchDate={item.launchDate}
          make={item.make}
          model={item.model}
          promotionList={promotionList}
          review={item.review}
          submodelList={item.subModels}
          onAlertModalSubmit={handleAlertModalSubmit}
          onComparedCheckboxChange={handleComparedCheckboxChange} />
      );
    });

    const promoIndexMap = {
      mobile: 1,
      ipadMini: 3,
      desktop: 2,
    } as const;

    type Mode = keyof typeof promoIndexMap;

    let currentMode: Mode | null = null;

    if (isDesktopMode) {
      currentMode = 'desktop';
    } else if (isIpadMiniMode) {
      currentMode = 'ipadMini';
    } else if (isMobileMode) {
      currentMode = 'mobile';
    }

    const promoIndex = currentMode && promoIndexMap[currentMode];

    const carListWithPromo = carList?.map((item, idx) => {
      if (idx === promoIndex) {
        if (promotionList) {
          if (promotionList.length > 0) {
            return [<PromotionCarousel
              key="promo"
              items={promotionList} />, item];
          }
          return [item];
        }
        return [<div
          key="promo-placeholder"
          style={{ height: '85px' }} />, item];
      }

      if (idx === 3 && !isDesktopMode) {
        return [
          <div
            key="google-ads-banner1"
            className={styles.containerAds}>
            <GoogleAds
              adUnitPath={listingBanner1.adUnitPath}
              divId={listingBanner1.divId}
              slot={listingBanner1.slot}
              targetingMap={{
                ...listingBanner1.targetingMap,
                ...searchTargetMapping,
              }}
              onSlotRenderEndedRef={onSlotRenderEndedRef} />
          </div>, item,
        ];
      }

      if (idx === 8 && !isDesktopMode) {
        return [
          <div
            key="google-ads-banner2"
            className={styles.containerAds}>
            <GoogleAds
              adUnitPath={listingBanner2.adUnitPath}
              divId={listingBanner2.divId}
              slot={listingBanner2.slot}
              targetingMap={{
                ...listingBanner2.targetingMap,
                ...searchTargetMapping,
              }}
              onSlotRenderEndedRef={onSlotRenderEndedRef} />
          </div>, item,
        ];
      }

      if (idx === 9 && isDesktopMode) {
        return [<TeadAds
          key="teads-ads"
          isApp={false}
          tagId={teadsTagId ?? ''}
          teadsId={teadsId ?? ''} />, item];
      }

      if (idx === 11 && !isDesktopMode) {
        return [<TeadAds
          key="teads-ads-mobile"
          isApp={appContextData?.isApp ?? false}
          tagId={teadsTagId ?? ''}
          teadsId={teadsId ?? ''} />, item];
      }

      return item;
    }).flat();

    if (promotionList
      && promotionList?.length > 0
      && !carListWithPromo?.some(
        item => isValidElement(item) && item.key === 'promo',
      )
    ) {
      carListWithPromo?.push(<PromotionCarousel
        key="promo"
        items={promotionList} />);
    }

    return carListWithPromo;
  }, [
    myAlertList,
    promotionList,
    list,
    isDesktopMode,
    isIpadMiniMode,
    isMobileMode,
    setMyAlert,
    onComparedCheckboxChange,
    compareItems,
    searchTargetMapping,
    appContextData?.isApp,
  ]);

  return (
    <div className={styles.containerRoot}>
      {carCardListWithPromo}
    </div>
  );
};
